// LIBRERIE/IMPORTS
import React, { useState, useCallback, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';

// CONFIGURAZIONI
import { appConfig } from "../../config/appConfig";
import mapStyles from '../../config/mapStyles.json';

// COMPONENTS
import ServerError from '../Error/ServerError';
import LoadingIndicator from '../Loaders/LoadingIndicator';

const GOOGLE_API_KEY = import.meta.env.VITE_APP_GOOGLE_API_KEY;

const mapCenter = {
    lat: Number(appConfig.map.centerMapData?.center_lat || appConfig.map.defaultCenter.lat),
    lng: Number(appConfig.map.centerMapData?.center_lng || appConfig.map.defaultCenter.lng),
};
const mapZoom = Number(appConfig.map.centerMapData?.zoom || appConfig.map.defaultZoom);

interface MarkerInfo {
    name: string;
    adr: string;
    lat: number;
    lng: number;
}

interface MapLoaderProps {
    markerInfo: MarkerInfo | null;
}

const MapLoader: React.FC<MapLoaderProps> = ({ markerInfo }) => {
    const { t } = useTranslation();
    const mapRef = useRef<google.maps.Map | null>(null);
    const [isInfoWindowOpen, setIsInfoWindowOpen] = useState(false);

    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_API_KEY || "",
        version: 'weekly',
        libraries: ['maps', 'marker'],
        language: 'it',
        region: 'IT'
    });

    const getMarkerIcon = useCallback((color: string): google.maps.Icon | undefined => {
        if (!isLoaded || !window.google || !window.google.maps) {
            return undefined;
        }
        return {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24"><path fill="${color}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 10c-1.65 0-3-1.35-3-3s1.35-3 3-3 3 1.35 3 3-1.35 3-3 3z"/></svg>`
            )}`,
            scaledSize: new window.google.maps.Size(40, 40),
            anchor: new window.google.maps.Point(20, 40),
        };
    }, [isLoaded]);

    const handleMarkerClick = useCallback(() => setIsInfoWindowOpen(prev => !prev), []);
    const handleInfoWindowClose = useCallback(() => setIsInfoWindowOpen(false), []);
    const onLoad = useCallback((map: google.maps.Map) => { mapRef.current = map; }, []);
    const onUnmount = useCallback(() => { mapRef.current = null; }, []);

    // Gestione degli stati di caricamento ed errore
    if (loadError) return <ServerError message={`${t('contactPage.errors.mapLoadFailed')} ${loadError.message}`} />;
    if (!GOOGLE_API_KEY) return <ServerError message="Missing Google Maps API Key." />;
    if (!isLoaded || !window.google || !window.google.maps) return <LoadingIndicator />

    return (
        <section className="property-map mb-5" data-location="true" style={{ minHeight: '400px' }}>
            <div className="map-canvas">
                {isLoaded ? (
                    <GoogleMap
                        mapContainerStyle={{ width: '100%', height: '100%' }}
                        center={mapCenter}
                        zoom={mapZoom}
                        onLoad={onLoad}
                        onUnmount={onUnmount}
                        options={{ styles: mapStyles, disableDefaultUI: false }}
                    >
                        {markerInfo && !isNaN(markerInfo.lat) && !isNaN(markerInfo.lng) && (
                            <Marker
                                position={{ lat: markerInfo.lat, lng: markerInfo.lng }}
                                icon={getMarkerIcon(appConfig.map.markerColor || '#FF0000')}
                                title={markerInfo.name}
                                onClick={handleMarkerClick}
                            />
                        )}
                        {isInfoWindowOpen && markerInfo && (
                            <InfoWindow
                                position={{ lat: markerInfo.lat, lng: markerInfo.lng }}
                                onCloseClick={handleInfoWindowClose}
                            >
                                <div>
                                    <h6>{markerInfo.name}</h6>
                                    <p>{markerInfo.adr}</p>
                                </div>
                            </InfoWindow>
                        )}
                    </GoogleMap>
                ) : (
                    <LoadingIndicator />
                )}
            </div>
        </section>
    );
};

export default MapLoader;